<template>
  <div>
    <div class="pt-6 visible">
      <h1
        v-if="!loading"
        class="text-lg leading-tight font-medium pb-2 xl:text-2.5xl xl:pb-4"
      >
        {{ $t('components.payment.title') }}
      </h1>

      <Loading v-if="loading && !errors.length">
        <p v-if="paymentProcessing" class="max-w-sm mx-auto text-center pt-6 xl:pt-12">
          {{ $t('components.payment.loading', brandInfo) }}
        </p>
      </Loading>

      <div v-if="errors.length" ref="error" class="w-full flex bg-au_red-100 text-au_error p-4 mt-2 mb-4">
        <div class="w-8 flex-shrink-0">
          <svg viewBox="0 0 21.82 21.82" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'w-5 h-5'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M11.52 15.91H10.3v-1.28h1.22zM11.43 13h-1V5.92h1z" fill="#8c1e33"/><path d="M10.91.78A10.13 10.13 0 1021 10.91 10.13 10.13 0 0010.91.78z" fill="none"/><path d="M10.91 0a10.91 10.91 0 1010.91 10.91A10.91 10.91 0 0010.91 0zm0 21A10.13 10.13 0 1121 10.91 10.12 10.12 0 0110.91 21z" fill="#8c1e33"/></svg>
        </div>
        <div class="pt-2px xl:pt-0">
          <p class="font-medium text-sm leading-none pb-2 xl:text-lg">
            {{ $t(`errors.type.${firstErrorKey}.title`, brandInfo) }}
          </p>
          <p class="text-sm xl:text-sm" v-html="$t(`errors.type.${firstErrorKey}.description`, brandInfo)" />
        </div>
      </div>

      <div v-if="!loading">
        <div v-if="!innerUltimateFailure" class="w-full mb-8">
          <p class="pb-4 leading-tight xl:text-xl">
            {{ $t('components.payment.description') }}
          </p>

          <div class="pb-2" :class="[formReady ? 'visible' : 'hidden']">
            <div class="mb-5">
              <TextInput
                v-model.trim="fields.name.value"
                :placeholder="$t('components.payment.form.name.placeholder')"
                :label="$t('components.payment.form.name.label')"
                :error="fields.name.valid ? '' : $t('components.payment.error.name')"
                @focus="resetFieldValidity('name')"
              />
            </div>

            <div class="w-full mb-5">
              <div
                class="input-component w-full"
                :class="{
                  'is-active': fields.cardNumber.focused || fields.cardNumber.filled,
                  'is-error': !fields.cardNumber.valid,
                }"
              >
                <div class="input relative secure-field">
                  <div id="card-number" />
                </div>

                <p v-if="!fields.cardNumber.valid">
                  <span class="text-au_error text-xs leading-none">
                    {{ $t('components.payment.error.cardNumber') }}
                  </span>
                </p>
              </div>
            </div>

            <div class="flex w-full mb-5">
              <div class="w-1/2 mr-4">
                <TextInput
                  v-model.trim="fields.expiryDate.value"
                  v-mask="'##/##'"
                  :placeholder="$t('components.payment.form.expiryDate.placeholder')"
                  :label="$t('components.payment.form.expiryDate.label')"
                  :error="fields.expiryDate.valid ? '' : $t('components.payment.error.expiryDate')"
                  @focus="resetFieldValidity('expiryDate')"
                />
              </div>
              <div class="w-1/2">
                <div
                  class="input-component w-full"
                  :class="{ 'is-active': fields.cvv.focused || fields.cvv.filled, 'is-error': !fields.cvv.valid }"
                >
                  <div class="input relative secure-field">
                    <div id="cvv-number" />
                  </div>

                  <p v-if="!fields.cvv.valid">
                    <span class="text-au_error text-xs leading-none">
                      {{ $t('components.payment.error.cvv') }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h1 class="text-lg leading-tight font-medium pb-2 xl:text-2.5xl xl:pb-4">
          {{ $t('components.summary.finalSummary.title') }}
        </h1>

        <div class="w-full mb-8">
          <div class="flex justify-between px-3 pt-3 pb-2 text-sm xl:px-4 xl:pt-4 xl:text-lg">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.totalDue') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.totalDue | lhFormatCurrency(currency, market) }}
            </div>
          </div>

          <div class="flex justify-between px-3 pt-2 pb-3 text-sm xl:px-4 xl:pb-4 xl:text-lg">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.flightVoucherBalance') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.vouchersBalance | lhFormatCurrency(currency, market) }}
            </div>
          </div>

          <div class="flex justify-between p-3 text-sm text-white bg-au_gray-600 xl:p-4 xl:text-lg">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.creditCardAmount') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.fromCard | lhFormatCurrency(currency, market) }}
            </div>
          </div>
        </div>

        <div class="flex xl:pt-6">
          <Button
            :loading="loading || secureFieldsProcessing || innerUltimateFailure"
            class="w-full xl:max-w-xs"
            @click="payment"
          >
            {{ $t('components.summary.submitButton') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Payment from '~psp-pages/base/redemption/payment.ts'

import Button from '~brand-components/austrian/button/button.vue'
import TextInput from '~brand-components/austrian/shared/text-input.vue'
import Loading from '~brand-components/austrian/loading/loading.vue'

export default Payment.extend({
  components: {
    Button,
    TextInput,
    Loading,
  },
  computed: {
    datatransStyles(): Record<string, string> {
      return {
        '*': `
          width: 100$;
          font-size: 1rem;
          color: #000000;
          background-color: #F1F3F5;
          height: 3.125rem;
          border: 0;
          padding: 0 1rem;
          border-radius: 0.25rem;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          margin: 0;
          outline: 0;
        `,
        '*::placeholder': 'color: rgba(120, 132, 158, 1); opacity: 1',
        '*:-ms-input-placeholder': 'color: #78849E',
      }
    },
  },
})

</script>

<style lang="scss" scoped>
.input-component {
  .input {
    @apply h-12.5;
  }
}
</style>
